import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Navigation2, DollarSign, X, Check } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { API } from "@/lib/api";
import { getCurrentPosition, haversineMiles } from "@/lib/geo";

/**
 * Compact bottom-sheet that pops up when a new delivery comes in.
 *
 * Replaces the full-screen redirect to /driver/earnings — the driver can now
 * see miles + time to pickup, miles + time of the trip, payout, and
 * Accept / Decline IN PLACE without losing the map they're on.
 *
 * Accept → POST /driver/accept-booking → navigate to /driver/track/{id}
 * Decline → just dismiss locally. The booking stays in the queue for the
 *           driver to reconsider later, OR (if the driver releases an active
 *           job) another driver / the same driver can re-pick it up.
 */

// Naive ETA estimate: 2 min per mile of road distance. Good enough for a
// driver-facing 5-second-glance card; LiveMap shows the precise route once
// the trip starts.
function estimateMinutes(miles) {
  if (!miles || miles <= 0) return null;
  return Math.max(2, Math.round(miles * 2));
}

export default function DriverJobOfferSheet() {
  const { isDriver, user } = useAuth();
  const { notifications, dismissNotification, markAsRead } = useNotifications();
  const navigate = useNavigate();
  const [driverPos, setDriverPos] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Get the most recent unread "new_delivery" notification with a booking attached
  const offer = useMemo(() => {
    return notifications.find(
      (n) => !n.read && n.type === "new_delivery" && n.data?.id && n.data?.pickup_coords,
    );
  }, [notifications]);

  // Pull the driver's GPS once the offer appears so we can compute the
  // pickup-distance. Re-prompted per offer; cached value is fine for ~30s
  // (driver isn't moving far between job pops).
  useEffect(() => {
    if (!offer) return;
    let cancelled = false;
    getCurrentPosition().then((pos) => {
      if (!cancelled) setDriverPos(pos);
    });
    return () => { cancelled = true; };
  }, [offer?.id]);

  if (!isDriver || !offer) return null;
  const job = offer.data;

  const tripMiles = Number(job.distance_miles) || 0;
  const tripMin = estimateMinutes(tripMiles);

  let pickupMiles = null;
  let pickupMin = null;
  if (driverPos && job.pickup_coords) {
    try {
      const miles = haversineMiles(driverPos, job.pickup_coords);
      if (typeof miles === "number" && miles > 0) {
        pickupMiles = miles;
        pickupMin = estimateMinutes(miles);
      }
    } catch { /* haversine quirks — leave as null */ }
  }

  // Payout math must match the tracking page's driver-view exactly:
  //   locked driver_payout > (subtotal × 0.80) + tip.
  // Prior code used `estimated_price` which is the customer TOTAL (includes
  // sales tax) — so the offer sheet was inflating the payout by ~6% and
  // never adding the tip. Sales tax goes to the state, not the driver.
  const payout = job.driver_payout != null
    ? Number(job.driver_payout)
    : (Number(job.subtotal ?? job.estimated_price ?? 0) * 0.8) + Number(job.tip ?? 0);

  const dismiss = () => {
    markAsRead(offer.id);
    dismissNotification(offer.id);
  };

  const accept = async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      await axios.post(`${API}/driver/accept-booking/${job.id}`);
      toast.success("Job accepted — heading to pickup");
      markAsRead(offer.id);
      dismissNotification(offer.id);
      navigate(`/driver/track/${job.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Try again";
      toast.error(`Couldn't accept: ${detail}`);
    } finally {
      setAccepting(false);
    }
  };

  const svcLabel = (job.service_type || "ride").replace(/_/g, " ");

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 pointer-events-none p-2 pb-[max(env(safe-area-inset-bottom),12px)]"
      data-testid="driver-job-offer-sheet"
    >
      <div
        className="pointer-events-auto max-w-lg mx-auto rounded-2xl bg-[#0c0c0c]/95 backdrop-blur-xl border border-[#0047FF]/40 shadow-[0_-8px_40px_rgba(0,71,255,0.25)] overflow-hidden animate-job-rise"
      >
        {/* Header strip */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0047FF]/12 border-b border-[#0047FF]/25">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#80B0FF]">
              New {svcLabel}
            </p>
          </div>
          <div className="flex items-center gap-1 text-emerald-400 text-sm font-bold" data-testid="offer-payout">
            <DollarSign className="w-3.5 h-3.5" />
            {payout.toFixed(2)}
            <span className="text-[9px] font-semibold text-emerald-300/70 ml-0.5 uppercase tracking-wider">payout</span>
          </div>
        </div>

        {/* Pickup / Dropoff metrics — the bit the user explicitly asked for */}
        <div className="grid grid-cols-2 divide-x divide-white/5">
          <div className="p-3" data-testid="offer-pickup-metrics">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">
              <Navigation2 className="w-3 h-3 text-[#80B0FF]" />
              To pickup
            </div>
            <p className="text-lg font-black text-white leading-none" style={{ fontFamily: "Outfit, system-ui" }}>
              {pickupMiles != null ? `${pickupMiles.toFixed(1)} mi` : "—"}
            </p>
            <p className="text-xs text-zinc-400 mt-1">
              {pickupMin != null ? `≈ ${pickupMin} min` : (driverPos ? "—" : "Allow location for ETA")}
            </p>
            <p className="text-[10px] text-zinc-500 mt-1.5 truncate" title={job.pickup_address}>
              {job.pickup_address || "—"}
            </p>
          </div>

          <div className="p-3" data-testid="offer-trip-metrics">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">
              <MapPin className="w-3 h-3 text-amber-400" />
              Trip
            </div>
            <p className="text-lg font-black text-white leading-none" style={{ fontFamily: "Outfit, system-ui" }}>
              {tripMiles ? `${tripMiles.toFixed(1)} mi` : "—"}
            </p>
            <p className="text-xs text-zinc-400 mt-1">
              {tripMin != null ? `≈ ${tripMin} min` : "—"}
            </p>
            <p className="text-[10px] text-zinc-500 mt-1.5 truncate" title={job.dropoff_address}>
              {job.dropoff_address || "—"}
            </p>
          </div>
        </div>

        {/* Action row */}
        <div className="grid grid-cols-5 gap-2 p-3 pt-2 bg-black/30">
          <button
            type="button"
            onClick={dismiss}
            className="col-span-2 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-zinc-800/80 border border-white/5 text-zinc-200 font-semibold text-sm hover:bg-zinc-700 transition-colors"
            data-testid="offer-decline-button"
          >
            <X className="w-4 h-4" />
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={accepting}
            className="col-span-3 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-[#0047FF] text-white font-bold text-sm hover:bg-[#003BE0] shadow-[0_4px_18px_-6px_rgba(0,71,255,0.7)] transition-colors disabled:opacity-60"
            data-testid="offer-accept-button"
          >
            <Check className="w-4 h-4" />
            {accepting ? "Accepting…" : "Accept job"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes job-rise {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-job-rise { animation: job-rise 0.25s ease-out forwards; }
      `}</style>
    </div>
  );
}
